import { useCallback, useMemo, useRef, useState } from 'react';

import { BottomSheet, Drop, Tag, useBreakpoint } from '@hh.ru/magritte-ui';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { AppStore } from 'src/app/store';
import { useNotification } from 'src/components/Notifications/Provider';
import resumeSidebarLoadError from 'src/components/Notifications/ResumeSidebar';
import translation from 'src/components/translation';
import { useOnOffState } from 'src/hooks/useOnOffState';
import { NegotiationsHistory } from 'src/models/negotiationsHistory';
import { fetcher } from 'src/utils/fetcher';

import MoreResponsesList from 'src/components/ResumeCard/Header/Tags/MoreResponses/MoreResponsesList';

import styles from './index.less';

const RESUME_HISTORY_URL = '/shards/resume/history';

interface QueryParams {
    resumeHash?: string;
    vacancyId?: string;
    maxEvents?: number;
}

declare global {
    interface FetcherGetApi {
        [RESUME_HISTORY_URL]: {
            queryParams: QueryParams;
            response: { vacancies: AppStore['negotiationsHistory'] };
        };
    }
}

interface MoreResponsesTagProps {
    resumeHash: string;
    vacancyId: number;
}

const TrlKeys = {
    label: 'moreResponsesTag.label',
};

const MoreResponsesTag: TranslatedComponent<MoreResponsesTagProps> = ({ resumeHash, vacancyId, trls }) => {
    const [dropOpen, openDrop, closeDrop] = useOnOffState(false);
    const { isMobile } = useBreakpoint();
    const activatorRef = useRef<HTMLSpanElement>(null);
    const params = useMemo<QueryParams>(() => ({ resumeHash, maxEvents: 1 }), [resumeHash]);
    const { addNotification } = useNotification();
    const [negotiationsHistory, setNegotiationsHistory] = useState<NegotiationsHistory[] | []>([]);

    const fetchResumeHistory = useCallback(() => {
        fetcher
            .get(RESUME_HISTORY_URL, { params })
            .then((result) => {
                const vacancies = result.vacancies.filter((item) => item['@vacancyId'] !== vacancyId);
                setNegotiationsHistory(vacancies);
                openDrop();
            })
            .catch(() => {
                addNotification(resumeSidebarLoadError);
                setNegotiationsHistory([]);
            });
    }, [params, addNotification, vacancyId, openDrop]);

    const onTagClick = () => {
        if (!dropOpen) {
            fetchResumeHistory();
        } else {
            closeDrop();
        }
    };

    return (
        <>
            <span
                data-qa="more_responses_tag"
                className={styles.tagClickable}
                onClick={onTagClick}
                data-interactive
                ref={activatorRef}
            >
                <Tag style="special">{trls[TrlKeys.label]}</Tag>
            </span>
            {!!negotiationsHistory.length && (
                <>
                    <Drop activatorRef={activatorRef} placement="bottom-center" visible={dropOpen} onClose={closeDrop}>
                        {<MoreResponsesList negotiationsHistory={negotiationsHistory} />}
                    </Drop>

                    <BottomSheet visible={isMobile && dropOpen} onClose={closeDrop}>
                        {<MoreResponsesList negotiationsHistory={negotiationsHistory} />}
                    </BottomSheet>
                </>
            )}
        </>
    );
};

export default translation(MoreResponsesTag);
